/**
 * Pagination — el pie de una tabla paginada.
 *
 * ⚠️ El total va formateado con el **locale del tenant**, no con `es-CL` fijo
 * como en el sistema. Es la misma corrección del #1016 y el #1086: un tenant
 * peruano tiene que leer `1,284` y no `1.284`. Se recibe por prop, igual que
 * `NumericInput`, para que el componente siga siendo presentacional.
 *
 * ⚠️ La ventana es de **diez páginas** y se recentra al topar con un extremo,
 * así que siempre se ofrecen diez cuando existen. Con tres —lo que traía el
 * sistema— un listado de 50 páginas obligaba a 17 clics en «Siguiente» para
 * llegar al final.
 *
 * 🎯 Y por eso la **primera y la última van siempre**, con una elipsis cuando
 * quedan fuera de la ventana: ampliar la ventana acorta el viaje, pero solo
 * saltar al extremo lo vuelve corto de verdad. Con 50 páginas, ir de la 1 a la
 * 50 pasa de 17 clics a **uno**.
 *
 * ⚠️ `aria-current="page"` en el botón de la página actual: es lo que la
 * distingue para el lector de pantalla. El color solo lo refuerza.
 */
import type { HTMLAttributes } from 'react'

export interface PaginationProps extends Omit<HTMLAttributes<HTMLElement>, 'className'> {
  page?: number
  pageSize?: number
  total?: number
  onPageChange?: (page: number) => void
  onPageSizeChange?: (size: number) => void
  sizes?: number[]
  /** El del tenant. Decide los separadores de miles del total. */
  locale?: string
  /** Cuántos números de página se ofrecen a la vez. */
  ventana?: number
  className?: string
}

export function Pagination({
  page = 1,
  pageSize = 25,
  total = 0,
  onPageChange,
  onPageSizeChange,
  sizes = [25, 50, 100],
  locale = 'es-CL',
  ventana = 10,
  className = '',
  ...rest
}: PaginationProps) {
  const paginas = Math.max(1, Math.ceil(total / pageSize))

  // La ventana se centra en la página actual y se RECENTRA al topar con un
  // extremo: sin ese segundo ajuste, en la página 1 se verían seis y no diez.
  let desde = Math.max(1, page - Math.floor(ventana / 2))
  const hasta = Math.min(paginas, desde + ventana - 1)
  desde = Math.max(1, hasta - ventana + 1)
  const numeros = Array.from({ length: hasta - desde + 1 }, (_, i) => desde + i)

  const botonPagina = (p: number) => (
    <button
      key={p}
      type="button"
      className={`cl-btn cl-btn--sm ${p === page ? 'cl-btn--secondary' : 'cl-btn--ghost'}`}
      aria-current={p === page ? 'page' : undefined}
      style={
        p === page
          ? {
              // Los dos son canales en este repo: sin el `rgb()` la página
              // actual quedaría sin resaltar y nada lo avisaría.
              background: 'rgb(var(--color-selected))',
              color: 'rgb(var(--color-primary))',
              fontWeight: 700,
            }
          : undefined
      }
      onClick={() => onPageChange?.(p)}
    >
      {p}
    </button>
  )

  return (
    <nav
      aria-label="Paginación"
      className={className}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
        padding: '11px 16px',
        borderTop: 'var(--border-hairline)',
        fontSize: 11.5,
        color: 'rgb(var(--color-content-subtle))',
        flexWrap: 'wrap',
      }}
      {...rest}
    >
      <span>
        Página {page} de {paginas} · {total.toLocaleString(locale)} resultados
        {onPageSizeChange && (
          <>
            {' · '}
            <select
              aria-label="Resultados por página"
              value={pageSize}
              onChange={e => onPageSizeChange(Number(e.target.value))}
              style={{
                background: 'transparent',
                color: 'inherit',
                border: 'none',
                font: 'inherit',
                cursor: 'pointer',
              }}
            >
              {sizes.map(s => (
                <option key={s} value={s}>
                  {s} por página
                </option>
              ))}
            </select>
          </>
        )}
      </span>
      {/* `flexWrap` porque con diez números más los extremos no entran en un
          teléfono, y aplastarlos dejaría los botones bajo el mínimo táctil. */}
      <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
        <button
          type="button"
          className="cl-btn cl-btn--secondary cl-btn--sm"
          disabled={page <= 1}
          onClick={() => onPageChange?.(page - 1)}
        >
          Anterior
        </button>

        {desde > 1 && (
          <>
            {botonPagina(1)}
            {/* La elipsis es decorativa: lo que se navega son los botones. */}
            {desde > 2 && <span aria-hidden="true" style={{ padding: '0 2px' }}>…</span>}
          </>
        )}

        {numeros.map(botonPagina)}

        {hasta < paginas && (
          <>
            {hasta < paginas - 1 && <span aria-hidden="true" style={{ padding: '0 2px' }}>…</span>}
            {botonPagina(paginas)}
          </>
        )}

        <button
          type="button"
          className="cl-btn cl-btn--secondary cl-btn--sm"
          disabled={page >= paginas}
          onClick={() => onPageChange?.(page + 1)}
        >
          Siguiente
        </button>
      </div>
    </nav>
  )
}
